import classnames from 'classnames/bind'
import { useEffect, useState } from 'react'

import NoResults from '~/components/PageWithFilters/NoResults'
import { IMAGE_SIZE } from '~/components/Slices/ImageWithLabel'
import FilteredHeaderBlock, {
  FilteredHeaderBlockProps,
} from '~/components/UI/FilteredProductsGrid/FilteredHeaderBlock'
import Results from '~/components/UI/FilteredProductsGrid/Results'
import Image, { imageLoaderStoryblok } from '~/components/UI/Image'
import { ProductCardProps } from '~/components/UI/ProductCard'
import {
  CARD_IMAGE_LOADER,
  CARD_PRODUCT_LAYOUT,
  CardImage,
} from '~/components/UI/ProductCard/types'

import { HITS_PER_PAGE } from '~/providers/FilterProvider'
import {
  LAYOUT,
  useLayoutSwitcherProvider,
} from '~/providers/LayoutSwitcherProvider'
import { usePagination } from '~/providers/PaginationProvider'

import { randomIntFromInterval } from '~/utils/maths/random'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export type FilteredProductsGridProps = {
  className?: string
  results?: ProductCardProps[]
  filteredHeaderBlock?: FilteredHeaderBlockProps
  currentPage?: number
  customLayout?: boolean
  hasResults?: boolean
  maxPage?: number
  randomImages?: CardImage[]
  withPriority?: boolean
}

export const HITS_CHUNKS = {
  [LAYOUT.BIG]: 3,
  [LAYOUT.MEDIUM]: 4,
  [LAYOUT.SMALL]: 6,
}

export const MOBILE_HITS_CHUNKS = {
  [LAYOUT.BIG]: 1,
  [LAYOUT.MEDIUM]: 2,
}

export const GRID_LAYOUT = {
  [LAYOUT.BIG]: CARD_PRODUCT_LAYOUT.BIG_GRID,
  [LAYOUT.MEDIUM]: CARD_PRODUCT_LAYOUT.MEDIUM_GRID,
  [LAYOUT.SMALL]: CARD_PRODUCT_LAYOUT.SMALL_GRID,
}

export const RANDOM_IMAGES_LENGTH = 2

const getUniqueIndex = ({ array, maxIndex }) => {
  const index = randomIntFromInterval(0, maxIndex)

  if (array.includes(index)) {
    return getUniqueIndex({ array, maxIndex })
  } else {
    return index
  }
}

function FilteredProductsGrid({
  className,
  results,
  hasResults,
  filteredHeaderBlock,
  customLayout,
  maxPage,
  randomImages,
  withPriority,
}: FilteredProductsGridProps) {
  const { step } = useLayoutSwitcherProvider()
  const { currentPage } = usePagination()
  const withCustomHeader = filteredHeaderBlock && currentPage === 1

  const [images, setImages] = useState<CardImage[]>(null)

  // if there are no randomImages from storyblok, we take the images hover from the products cards
  const randomImagesBank =
    randomImages ??
    results
      ?.filter((result) => result?.imageHover?.data)
      ?.map((result) => result?.imageHover)

  const getRandomImagesIndexes = (maxIndex: number): number[] => {
    return Array(RANDOM_IMAGES_LENGTH)
      ?.fill(null)
      ?.reduce((acc, curr, _, initial) => {
        const index =
          initial?.length > maxIndex + 1
            ? randomIntFromInterval(0, maxIndex)
            : getUniqueIndex({ array: acc, maxIndex: maxIndex })
        return [...acc, index]
      }, [])
  }

  useEffect(() => {
    if (
      currentPage > 1 &&
      currentPage < maxPage &&
      customLayout &&
      !withCustomHeader &&
      randomImagesBank?.length > 0 &&
      results?.length === HITS_PER_PAGE.FILTERED
    ) {
      const randomIndexes = getRandomImagesIndexes(randomImagesBank?.length - 1)
      setImages(
        randomIndexes
          ?.map((index) => randomImagesBank?.[index] ?? null)
          ?.filter((item) => item),
      )
    } else {
      setImages(null)
    }
  }, [currentPage, results])

  return (
    <div
      className={cx(css.ProductsGrid, className, css?.[step], {
        customLayout: Boolean(customLayout || withCustomHeader),
      })}>
      {withCustomHeader && (
        <div className={css.customDetails}>
          <FilteredHeaderBlock
            className={css.custom}
            {...filteredHeaderBlock}
          />
        </div>
      )}
      {hasResults ? (
        <Results
          layout={GRID_LAYOUT[step]}
          items={results}
          withPriority={withPriority}
        />
      ) : (
        <NoResults className={css.noResults} />
      )}
      {images &&
        images?.length > 0 &&
        images?.map((image, i) => {
          return (
            <div
              key={`${image?.data?.src}-${i}`}
              className={css.imageContainer}
              style={{
                gridColumn: i * 2 + 1,
                gridRow: i * 2 + 2,
              }}>
              <Image
                className={cx(css.image)}
                draggable={false}
                sizesFromBreakpoints={IMAGE_SIZE[GRID_LAYOUT[step]]}
                layout="fill"
                objectFit="cover"
                asPlaceholder
                loader={
                  image?.loaderType === CARD_IMAGE_LOADER.STORYBLOK
                    ? imageLoaderStoryblok
                    : null
                }
                {...image?.data}
              />
            </div>
          )
        })}
    </div>
  )
}
export default FilteredProductsGrid
